import { Injectable } from '@angular/core';
import {
  ActionContributionsService,
  AWFAction,
} from '@ansys-internal/awf-angular/contributions';
import { StaticRequirement, UnitType } from '../../../api';
import { RequirementsService } from '../../shared/services/requirements.service';
import { CapabilityCurveTypeEnum } from '../../shared/enums/capability-curve-type.enum';
import { RequirementPlotType } from '../../shared/enums/reqirement-plot-type.enum';
import { ConvertUnitValuesAction } from '../convert-unit-values/convert-unit-values.action';
import { ConceptUnitService } from '../../shared/services/unit.service';

type InputType = [any[], boolean?];
type ReturnType = [
  any,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number
];

@Injectable({
  providedIn: 'root',
})
export class ProcessResultDataAction
  implements AWFAction<InputType, ReturnType>
{
  private maxSpeed = 0;
  private maxTorque = 0;
  private maxPower = 0;
  private maxAcceleration = 0;
  private minSpeed: number = Infinity;
  private minTorque: number = Infinity;
  private minPower: number = Infinity;
  private minAcceleration: number = Infinity;

  constructor(
    private actions: ActionContributionsService,
    private requirementService: RequirementsService,
    private unitChoices: ConceptUnitService
  ) {}

  async execute(context: InputType): Promise<ReturnType> {
    this.resetLimits();

    const [results, isDynamic] = context;
    const data: Partial<any>[] = [];

    let convertedSpeeds: number[] = [];

    for (const req of results) {
      if (req.error_code || !req.capability_curve) continue;

      const color: string = 'grey';
      const lineColor: string = 'grey';

      [convertedSpeeds, this.minSpeed, this.maxSpeed] =
        await this.actions.execute(ConvertUnitValuesAction, [
          req.capability_curve?.speeds,
          UnitType.SPEED,
          this.minSpeed,
          this.maxSpeed,
        ]);

      const convertedValues = await this.convertYAxisValues(req);

      const { convertedReqSpeeds, convertedReqValues } = isDynamic
        ? await this.convertDynamicRequirements(req)
        : await this.convertStaticRequirements(req);

      // Build data points for capability and requirement
      const capabilityLabel = (!isDynamic && req.requirement.steady_state_capability_curve)
        ? `${req.requirement.name} Thermal Envelope`
        : `${req.requirement.name} Capability`;

      data.push(
        {
          type: 'scatter',
          x: convertedSpeeds,
          y: convertedValues,
          line: { color: lineColor },
          name: capabilityLabel,
          _type: RequirementPlotType.CAPABILITY_CURVE,
          id: req.id,
        },
        {
          type: 'scatter',
          marker: isDynamic ? { color } : { size: 20, color },
          x: convertedReqSpeeds,
          y: convertedReqValues,
          name: `${req.requirement.name}`,
          _type: RequirementPlotType.REQUIREMENT,
          id: req.id,
        }
      );

      // Add orange triangle markers for any error points on the capability curve
      if (!isDynamic && req.capability_curve?.errors && Object.keys(req.capability_curve.errors).length > 0) {
        const errorKeys = Object.keys(req.capability_curve.errors).map(Number);
        data.push({
          type: 'scatter',
          name: 'Capability Curve Errors',
          x: errorKeys.map((i) => convertedSpeeds[i]),
          y: errorKeys.map((i) => convertedValues[i]),
          marker: {
            color: '#ff8c00',
            symbol: 'triangle-up',
            size: 10,
            line: { color: 'black', width: 1 },
          },
          id: req.id,
          mode: 'markers',
        });
      }
    }

    return [
      data,
      this.maxSpeed,
      this.maxTorque,
      this.maxPower,
      this.maxAcceleration,
      this.minSpeed,
      this.minTorque,
      this.minPower,
      this.minAcceleration,
    ];
  }

  private async convertYAxisValues(req: any): Promise<number[]> {
    let convertedValues: number[] = [];
    const hasError = !!req.capability_curve?.error_code;
    const yAxisValue = this.mapYAxisValue(req.requirement);
    const unitType = this.getUnitType();

    // Always update min/max from the requirement point so the axis range includes it
    const reqValue = this.unitChoices.convertUnit(unitType, yAxisValue as any);
    switch (this.requirementService.requirementYAxis()) {
      case CapabilityCurveTypeEnum.POWER:
        this.minPower = Math.min(this.minPower, reqValue);
        this.maxPower = Math.max(this.maxPower, reqValue * 1.05);
        break;
      case CapabilityCurveTypeEnum.TORQUE:
        this.minTorque = Math.min(this.minTorque, reqValue);
        this.maxTorque = Math.max(this.maxTorque, reqValue * 1.05);
        break;
      case CapabilityCurveTypeEnum.ACCELERATION:
        this.minAcceleration = Math.min(this.minAcceleration, reqValue);
        this.maxAcceleration = Math.max(this.maxAcceleration, reqValue * 1.05);
        break;
    }

    if (hasError) {
      return convertedValues;
    }

    switch (this.requirementService.requirementYAxis()) {
      case CapabilityCurveTypeEnum.POWER:
        [convertedValues, this.minPower, this.maxPower] =
          await this.actions.execute(ConvertUnitValuesAction, [
            req.capability_curve.powers,
            UnitType.POWER,
            this.minPower,
            this.maxPower,
          ]);
        break;
      case CapabilityCurveTypeEnum.TORQUE:
        [convertedValues, this.minTorque, this.maxTorque] =
          await this.actions.execute(ConvertUnitValuesAction, [
            req.capability_curve.torques,
            UnitType.TORQUE,
            this.minTorque,
            this.maxTorque,
          ]);
        break;
      case CapabilityCurveTypeEnum.ACCELERATION:

        if (req.capability_curve?.accelerations) {
          [convertedValues, this.minAcceleration, this.maxAcceleration] =
            await this.actions.execute(ConvertUnitValuesAction, [
              req.capability_curve.accelerations,
              UnitType.ACCELERATION,
              this.minAcceleration,
              this.maxAcceleration,
            ]);
        }
        break;
      default:
        throw new Error('Unknown requirementYAxis value');
    }
    return convertedValues;
  }

  private async convertDynamicRequirements(req: any): Promise<any> {
    const [convertedReqSpeeds, ,] = await this.actions.execute(
      ConvertUnitValuesAction,
      [
        req.requirements.map((staticReq: StaticRequirement) => staticReq.speed),
        UnitType.SPEED,
        this.minSpeed,
        this.maxSpeed,
      ]
    );

    const [convertedReqValues, ,] = await this.actions.execute(
      ConvertUnitValuesAction,
      [
        req.requirements.map((staticReq: StaticRequirement) =>
          this.mapYAxisValue(staticReq)
        ),
        this.getUnitType(),
        this.minPower,
        this.maxPower,
      ]
    );

    return { convertedReqSpeeds, convertedReqValues };
  }

  private async convertStaticRequirements(req: any): Promise<any> {
    const convertedReqSpeeds = Array.isArray(req.requirement.speed)
      ? req.requirement.speed.map((speed: number) =>
          this.unitChoices.convertUnit(UnitType.SPEED, speed)
        )
      : [this.unitChoices.convertUnit(UnitType.SPEED, req.requirement.speed)];

    // Track speed min/max from the requirement marker
    for (const s of convertedReqSpeeds) {
      this.minSpeed = Math.min(this.minSpeed, s);
      this.maxSpeed = Math.max(this.maxSpeed, s * 1.05);
    }

    const yAxisValues = this.mapYAxisValue(req.requirement);
    const convertedReqValues = Array.isArray(yAxisValues)
      ? yAxisValues.map((value: number) =>
          this.unitChoices.convertUnit(this.getUnitType(), value)
        )
      : [this.unitChoices.convertUnit(this.getUnitType(), yAxisValues)];

    return { convertedReqSpeeds, convertedReqValues };
  }

  private mapYAxisValue(requirement: any): number[] {
    switch (this.requirementService.requirementYAxis()) {
      case CapabilityCurveTypeEnum.POWER:
        return requirement.total_tractive_power;
      case CapabilityCurveTypeEnum.TORQUE:
        return requirement.total_tractive_torque;
      case CapabilityCurveTypeEnum.ACCELERATION:
        return requirement.acceleration;
      default:
        throw new Error('Unknown requirementYAxis value');
    }
  }

  private getUnitType(): UnitType {
    switch (this.requirementService.requirementYAxis()) {
      case CapabilityCurveTypeEnum.POWER:
        return UnitType.POWER;
      case CapabilityCurveTypeEnum.TORQUE:
        return UnitType.TORQUE;
      case CapabilityCurveTypeEnum.ACCELERATION:
        return UnitType.ACCELERATION;
      default:
        throw new Error('Unknown requirementYAxis value');
    }
  }

  private resetLimits(): void {
    this.maxSpeed = 0;
    this.maxTorque = 0;
    this.maxPower = 0;
    this.maxAcceleration = 0;
    this.minSpeed = Infinity;
    this.minTorque = Infinity;
    this.minPower = Infinity;
    this.minAcceleration = Infinity;
  }
}
